'use client';

import type { ButtonHTMLAttributes } from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { GARAN_HEADLINE } from '@packages/shared/src/ui/Garan/garan.config';
import { GaranLabel } from '@packages/shared/src/ui/Garan/GaranLabel/GaranLabel';
import { GaranNestedLabel } from '@packages/shared/src/ui/Garan/GaranNestedLabel/GaranNestedLabel';
import type { GaranDefaultProps } from '@packages/shared/src/ui/Garan/garan.types';

import { DialogHeader } from '../Dialog/DialogHeader/DialogHeader.client';

/**
 * Props for the interactive Garan warranty label.
 *
 * @property warranty - Warranty duration in years, comma separated when needed.
 * @property brand - Brand name displayed in the warranty dialog.
 * @property model - Model name displayed in the warranty dialog.
 * @property className - Optional CSS classes applied to the trigger button.
 */
export type GaranProps = Omit<GaranDefaultProps, 'localizedDescription'> &
  Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'className'>;

/**
 * Renders the compact Garan warranty label and opens the full label in a dialog
 * when the trigger is activated.
 *
 * The size of the trigger can be adjusted using the `className` prop, which is applied to the button element.
 *
 * @param props - Warranty details and optional trigger button classes.
 */
export const Garan = ({ className, ...props }: GaranProps) => {
  const { formatMessage } = useIntl();

  const [isOpen, setIsOpen] = useState(false);

  const localizedDescription = formatMessage({
    id: 'garan.localizedDescription',
    defaultMessage: 'Herstellergarantie in Jahren',
  });

  const closeDialog = () => {
    setIsOpen(false);
  };

  const openDialog = () => {
    setIsOpen(true);
  };

  return (
    <>
      <button className={className} onClick={openDialog} type="button" aria-label={GARAN_HEADLINE}>
        <GaranNestedLabel localizedDescription={localizedDescription} warranty={props.warranty} />
      </button>
      {isOpen && (
        <Dialog isOpen onClose={closeDialog} variant="bottomSheet">
          <DialogHeader onClose={closeDialog}>{GARAN_HEADLINE}</DialogHeader>
          <DialogContent className="md:min-w-xl">
            <GaranLabel {...props} localizedDescription={localizedDescription} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
